import type { KnockoutNode, KnockoutTree, Match, Team } from '@/types/tournament.model';
import { formatShortDate, formatTime } from '@/utils/tv-mode-data';
import { getTvTeamFaceSlots, type TVTeamFaceSlot } from './tv-team-faces';
import { TV_PALETTE } from './tv-mode.constants';
import { TVAutoScrollViewport } from './TVAutoScrollViewport';
import { useTVScale } from './TVScaleContext';
import { TVTeamAvatarStack } from './TVTeamAvatarStack';

/**
 * En el bracket, cuando una posición aún no tiene equipo definido los slots
 * vienen con iniciales placeholder ("P1"/"P2"). Para el cuadro eliminatorio
 * preferimos mostrar "--" — es más neutral y deja claro que la plaza está
 * por definir.
 */
const placeholderToDashes = (
  slots: [TVTeamFaceSlot, TVTeamFaceSlot],
): [TVTeamFaceSlot, TVTeamFaceSlot] =>
  slots.map((slot) =>
    !slot.imageUrl && (slot.initials === 'P1' || slot.initials === 'P2')
      ? { ...slot, initials: '--' }
      : slot,
  ) as [TVTeamFaceSlot, TVTeamFaceSlot];

const PLACEHOLDER = 'Por definir';

/**
 * Verde "ganador" para destacar al equipo que avanza dentro del cuadro
 * eliminatorio (alineado con la convención visual: verde = gana). Usamos
 * el verde fuerte del header (TV_PALETTE.accent) más una versión más oscura
 * para el score, así contrasta sobre el tinte verde del row.
 */
const BRACKET_WINNER = TV_PALETTE.accent;
const BRACKET_WINNER_STRONG = '#2FA821';

const isPlaceholderName = (name: string): boolean =>
  /^\d+[°º]?\s*Grupo\b/i.test(name.trim());

const buildTeamLabel = (
  getTeamName: (team: Team | undefined | null) => string,
) =>
  (team: Team | undefined): string => {
    if (!team?.players?.length) return PLACEHOLDER;
    const raw = team.players
      .map((p) => p?.name?.trim())
      .filter((n): n is string => !!n);
    if (raw.length === 0) return PLACEHOLDER;
    if (raw.every(isPlaceholderName)) return raw.join(' / ');
    return getTeamName(team);
  };

interface Props {
  tree: KnockoutTree | null | undefined;
  accent: string;
}

type RoundEntry = {
  match: Match | undefined;
  advancingTeam?: Team;
  roundName: string;
};

/**
 * Una entrada se considera "resuelta" si:
 *  - es un BYE (sin match pero con equipo que avanza), o
 *  - el partido tiene resultado válido cargado.
 */
const isEntryResolved = (e: RoundEntry): boolean => {
  if (!e.match) return !!e.advancingTeam;
  return e.match.result?.isValid === true;
};

const isRoundComplete = (round: RoundEntry[]): boolean =>
  round.length > 0 && round.every(isEntryResolved);

/**
 * Selecciona qué rondas mostrar en pantalla:
 *  - Si todas las rondas están jugadas → semi + final (modo "campeón").
 *  - Si solo hay una ronda → esa ronda sola.
 *  - Si la primera incompleta es la final → semi + final.
 *  - Si no, el "par activo" = [primera ronda incompleta, siguiente].
 */
const selectVisibleRounds = (
  rounds: RoundEntry[][],
): { visible: RoundEntry[][]; championMode: boolean } => {
  if (rounds.length === 0) return { visible: [], championMode: false };
  if (rounds.length === 1) return { visible: rounds, championMode: false };

  const lastIdx = rounds.length - 1;
  const firstIncomplete = rounds.findIndex((r) => !isRoundComplete(r));
  if (firstIncomplete === -1) {
    return {
      visible:
        rounds.length >= 2 ? [rounds[lastIdx - 1], rounds[lastIdx]] : [rounds[lastIdx]],
      championMode: true,
    };
  }
  if (firstIncomplete === lastIdx) {
    return {
      visible: lastIdx >= 1 ? [rounds[lastIdx - 1], rounds[lastIdx]] : [rounds[lastIdx]],
      championMode: false,
    };
  }
  const next = rounds[firstIncomplete + 1];
  return {
    visible: next ? [rounds[firstIncomplete], next] : [rounds[firstIncomplete]],
    championMode: false,
  };
};

const flatten = (
  node: KnockoutNode | null | undefined,
  acc: KnockoutNode[] = [],
): KnockoutNode[] => {
  if (!node) return acc;
  acc.push(node);
  flatten(node.leftChild, acc);
  flatten(node.rightChild, acc);
  return acc;
};

const ROUND_NAMES: Record<number, string> = {
  1: 'Octavos',
  2: 'Cuartos',
  3: 'Semifinal',
  4: 'Final',
};

const buildRounds = (tree: KnockoutTree | null | undefined): RoundEntry[][] => {
  if (!tree?.rootNode) return [];
  const nodes = flatten(tree.rootNode);
  const byRound = new Map<number, KnockoutNode[]>();
  let maxRound = 0;
  nodes.forEach((n) => {
    if (!n.round) return;
    if (!byRound.has(n.round)) byRound.set(n.round, []);
    byRound.get(n.round)!.push(n);
    maxRound = Math.max(maxRound, n.round);
  });

  if (maxRound === 0 && tree.rootNode.round) maxRound = tree.rootNode.round;
  if (maxRound === 0) return [];

  const rounds: RoundEntry[][] = [];
  for (let r = 1; r <= maxRound; r++) {
    const entries = (byRound.get(r) ?? []).map<RoundEntry>((n) => ({
      match: n.match,
      advancingTeam: n.advancingTeam,
      roundName:
        n.roundName ??
        (maxRound <= 4
          ? ROUND_NAMES[r + (4 - maxRound)] ?? `Ronda ${r}`
          : r === maxRound
            ? 'Final'
            : `Ronda ${r}`),
    }));
    rounds.push(entries);
  }
  return rounds;
};

const isLiveMatch = (match: Match | undefined): boolean => {
  if (!match) return false;
  if (match.result?.isValid) return false;
  const start = new Date(match.startDate).getTime();
  if (Number.isNaN(start)) return false;
  return start <= Date.now();
};

type SetScore = { team1Games: number; team2Games: number };

const Row = ({
  team,
  faceSlots,
  sets,
  side,
  won,
  live,
  accent,
  badge,
  walkover,
}: {
  team: string;
  faceSlots: [TVTeamFaceSlot, TVTeamFaceSlot];
  sets: SetScore[] | null;
  side: 1 | 2;
  won: boolean;
  live: boolean;
  accent: string;
  badge: string | null;
  walkover?: boolean;
}) => {
  const { s } = useTVScale();
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${s(9)}px ${s(12)}px`,
        background: won ? `${BRACKET_WINNER}1F` : 'transparent',
        borderLeft: `3px solid ${won ? BRACKET_WINNER : 'transparent'}`,
        gap: s(10),
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: s(10),
          minWidth: 0,
          flex: 1,
        }}
      >
        <TVTeamAvatarStack
          slots={faceSlots}
          size={s(24)}
          dimmed={!won}
        />
        <span
          style={{
            fontSize: s(13),
            fontWeight: won ? 700 : 500,
            color: won ? TV_PALETTE.navy : TV_PALETTE.textSubtle,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            textDecoration: walkover ? 'line-through' : 'none',
            textDecorationThickness: walkover ? 2 : undefined,
          }}
        >
          {team}
        </span>
      </div>
      {badge ? (
        <span
          style={{
            fontSize: s(9),
            fontWeight: 800,
            marginLeft: s(10),
            padding: `${s(2)}px ${s(7)}px`,
            borderRadius: 4,
            background: `${BRACKET_WINNER}26`,
            color: BRACKET_WINNER_STRONG,
            letterSpacing: '0.08em',
          }}
        >
          {badge}
        </span>
      ) : sets && sets.length > 0 ? (
        <div
          className="font-space"
          style={{
            display: 'flex',
            gap: s(6),
            marginLeft: s(10),
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.01em',
          }}
        >
          {sets.map((set, i) => {
            const own = side === 1 ? set.team1Games : set.team2Games;
            const other = side === 1 ? set.team2Games : set.team1Games;
            const wonSet = own > other;
            return (
              <span
                key={i}
                style={{
                  fontSize: s(18),
                  fontWeight: 700,
                  color: wonSet ? BRACKET_WINNER_STRONG : TV_PALETTE.textGhost,
                }}
              >
                {own}
              </span>
            );
          })}
        </div>
      ) : live ? (
        <div style={{ display: 'flex', gap: s(3) }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: s(5),
                height: s(5),
                borderRadius: '50%',
                background: accent,
                animation: `tv-pulse 1.2s ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const Card = ({
  match,
  advancingTeam,
  accent,
  champion,
}: {
  match: Match | undefined;
  advancingTeam: Team | undefined;
  accent: string;
  champion?: boolean;
}) => {
  const { s, getTeamName } = useTVScale();
  const teamLabel = buildTeamLabel(getTeamName);
  const isBye = !match && !!advancingTeam;
  const live = !isBye && isLiveMatch(match);
  const team1 = isBye ? teamLabel(advancingTeam) : teamLabel(match?.team1);
  const team2 = isBye ? '' : teamLabel(match?.team2);
  const team1Faces = placeholderToDashes(
    getTvTeamFaceSlots(isBye ? advancingTeam : match?.team1),
  );
  const team2Faces = placeholderToDashes(getTvTeamFaceSlots(match?.team2));
  const result = match?.result;
  const setScores = result?.setScores ?? null;
  /**
   * Walkover: si un equipo no se presenta, gana el otro aunque el score
   * quede 0-0. `team1Walkover === true` significa "team1 abandonó/no jugó".
   * Si ambos abandonan, no destacamos a nadie.
   */
  const team1WO = result?.team1Walkover === true;
  const team2WO = result?.team2Walkover === true;
  const doubleWO = team1WO && team2WO;
  const team1Won = isBye
    ? true
    : result && !doubleWO
      ? team2WO ||
        (!team1WO && (result.team1Score ?? 0) > (result.team2Score ?? 0))
      : false;
  const team2Won =
    result && !doubleWO
      ? team1WO ||
        (!team2WO && (result.team2Score ?? 0) > (result.team1Score ?? 0))
      : false;
  const team1WonByWO = team1Won && team2WO;
  const team2WonByWO = team2Won && team1WO;

  const championBorder = champion ? '#FFC95A' : null;
  const championShadow = champion
    ? '0 0 22px rgba(255,201,90,.35), 0 1px 6px rgba(2,51,115,.08)'
    : null;

  /**
   * Hora del partido bajo la card (solo si hay match real con startDate
   * válido — los BYE quedan sin hora).
   */
  const startMs = match?.startDate ? new Date(match.startDate).getTime() : NaN;
  const hasValidStart =
    !isBye && !!match?.startDate && !Number.isNaN(startMs);
  const timeLabel = hasValidStart ? formatTime(match!.startDate) : null;
  const dateLabel = hasValidStart ? formatShortDate(match!.startDate) : null;

  return (
    <div
      style={{
        background: TV_PALETTE.card,
        border: `${champion ? 2 : 1}px solid ${championBorder ?? (live ? TV_PALETTE.accent2 : TV_PALETTE.border)}`,
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow:
          championShadow ??
          (live
            ? '0 0 14px rgba(255,90,29,0.12)'
            : '0 1px 4px rgba(2,51,115,0.06)'),
        minWidth: s(200),
      }}
    >
      {timeLabel && (
        <div
          style={{
            background: '#fff',
            borderBottom: `1px solid ${TV_PALETTE.border}`,
            padding: `${s(5)}px ${s(12)}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: s(8),
          }}
        >
          <span
            className="font-space"
            style={{
              fontSize: s(18),
              fontWeight: 700,
              color: TV_PALETTE.navy,
              letterSpacing: '0.02em',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {timeLabel}
          </span>
          {dateLabel && (
            <span
              style={{
                fontSize: s(10),
                fontWeight: 700,
                color: TV_PALETTE.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                lineHeight: 1,
              }}
            >
              {dateLabel}
            </span>
          )}
        </div>
      )}
      {champion && (
        <div
          style={{
            background: 'linear-gradient(90deg, #FFC95A 0%, #FF5A1D 100%)',
            color: '#fff',
            fontSize: s(10),
            fontWeight: 900,
            letterSpacing: '0.18em',
            textAlign: 'center',
            padding: `${s(3)}px 0`,
            textTransform: 'uppercase',
          }}
        >
          🏆 Campeón
        </div>
      )}
      <Row
        team={team1}
        faceSlots={team1Faces}
        sets={team1WonByWO ? null : setScores}
        side={1}
        won={team1Won}
        live={live}
        accent={accent}
        badge={isBye ? 'BYE' : team1WonByWO ? 'WO' : null}
        walkover={team1WO}
      />
      <div style={{ height: 1, background: TV_PALETTE.borderSoft }} />
      {isBye ? (
        // Placeholder con la misma altura intrínseca de un Row real
        // (avatar s(24) + padding s(9)*2) para que las cards de BYE no
        // rompan la grilla del bracket.
        <div
          aria-hidden
          style={{
            padding: `${s(9)}px ${s(12)}px`,
            height: s(24),
            boxSizing: 'content-box',
          }}
        />
      ) : (
        <Row
          team={team2}
          faceSlots={team2Faces}
          sets={team2WonByWO ? null : setScores}
          side={2}
          won={team2Won}
          live={live}
          accent={accent}
          badge={team2WonByWO ? 'WO' : null}
          walkover={team2WO}
        />
      )}
    </div>
  );
};

export const TVBracket = ({ tree, accent }: Props) => {
  const { s } = useTVScale();
  const rounds = buildRounds(tree);
  const { visible, championMode } = selectVisibleRounds(rounds);

  if (visible.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TV_PALETTE.textMuted,
          fontSize: s(13),
          padding: s(20),
          textAlign: 'center',
        }}
      >
        Cuadro de eliminatorias no generado todavía
      </div>
    );
  }

  /**
   * Layout en CSS Grid: 1 fila para los headers + N filas para las cards.
   * Col 0 ocupa 1 fila por card. Col 1 (siguiente ronda) tiene la mitad de
   * cards y cada una abarca 2 filas con `align-self: center`, lo que las
   * deja centradas verticalmente entre los dos cruces que la alimentan
   * (sin necesidad de medir alturas en JS).
   */
  const numCols = visible.length;
  const col0Count = visible[0].length;

  return (
    <TVAutoScrollViewport>
      <div
        style={{
          padding: `${s(8)}px ${s(8)}px`,
          display: 'grid',
          gridTemplateColumns: `repeat(${numCols}, minmax(0, 1fr))`,
          columnGap: s(24),
          rowGap: s(10),
          alignItems: 'stretch',
          maxWidth: numCols === 1 ? s(360) : undefined,
          margin: numCols === 1 ? '0 auto' : undefined,
        }}
      >
        {/* Headers de columna */}
        {visible.map((round, ri) => {
          const isFinal = championMode && ri === numCols - 1;
          return (
            <div
              key={`h-${ri}`}
              style={{
                gridColumn: ri + 1,
                gridRow: 1,
                textAlign: 'center',
                fontSize: s(16),
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                color: isFinal ? TV_PALETTE.accent2 : TV_PALETTE.navy,
                paddingBottom: s(8),
              }}
            >
              {round[0]?.roundName ?? ''}
            </div>
          );
        })}

        {/* Cards: cada columna r > 0 spannea 2 filas por card y se centra. */}
        {visible.map((round, ri) =>
          round.map((entry, mi) => {
            const span = 1 << ri; // col0 spanea 1, col1 spanea 2, col2 spanea 4…
            const startRow = 2 + mi * span;
            const isFinalCard = championMode && ri === numCols - 1;
            return (
              <div
                key={`c-${ri}-${mi}`}
                style={{
                  gridColumn: ri + 1,
                  gridRow: `${startRow} / span ${span}`,
                  alignSelf: 'center',
                }}
              >
                <Card
                  match={entry.match}
                  advancingTeam={entry.advancingTeam}
                  accent={accent}
                  champion={isFinalCard}
                />
              </div>
            );
          }),
        )}

        {/* Filler invisible para que la altura total del grid ancle el
            spacing de col0 incluso cuando col1 tiene menos cards. */}
        <div
          aria-hidden
          style={{
            gridColumn: 1,
            gridRow: `${2 + col0Count} / auto`,
            height: 0,
          }}
        />
      </div>
    </TVAutoScrollViewport>
  );
};

